import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  agentInstallShellQuery,
  saveAgentInstallGeneral,
  saveAgentInstallVisual,
  type AgentInstallConfig,
  type AgentInstallShell,
} from '../../api/settingsAgentInstall'
import { Forbidden } from '../../ui/Forbidden'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings -> AGENT SETTINGS -> Install Options, panou cu panou fata de
 * `views/version-3/settings/agent/install_options.php`.
 *
 * DOUA PANOURI, DOUA SALVARI. Asa era si acolo: doua formulare, doua butoane
 * "Save", doua capete. Nu s-au unit, fiindca scriu in tabele diferite si
 * fiindca un singur buton ar fi scris si ce n-a atins nimeni.
 *
 * CE SE VEDE DUPA SALVARE E CE S-A SCRIS. In portal, `ajaxForm` arata un
 * toastr si lasa campurile cum erau tastate - iar culoarea goala se scria ca
 * `#DDDDDD` fara ca ecranul s-o arate. Aici fiecare salvare raspunde cu ecranul
 * intreg.
 */

function failed(error: unknown): string {
  return error instanceof ApiError && error.message ? error.message : 'Something went wrong.'
}

function Check({
  id,
  label,
  checked,
  disabled,
  onChange,
}: {
  id: string
  label: string
  checked: boolean
  disabled: boolean
  onChange(next: boolean): void
}) {
  return (
    <tr>
      <td className="pl-2">
        <label htmlFor={id} style={{ fontWeight: 'normal', marginBottom: 0 }}>
          {label}
        </label>
      </td>
      <td className="pr-2 align-right">
        <input
          type="checkbox"
          id={id}
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
      </td>
    </tr>
  )
}

export function AgentInstallPage() {
  const query = useQueryClient()
  const shell = useQuery(agentInstallShellQuery)
  const [live, setLive] = useState<AgentInstallShell | null>(null)
  const [config, setConfig] = useState<AgentInstallConfig | null>(null)
  const [busy, setBusy] = useState('')

  const page = live ?? shell.data ?? null

  useEffect(() => {
    if (page && !config) setConfig(page.settings)
  }, [page, config])

  if (shell.error instanceof ApiError && shell.error.status === 403) return <Forbidden />
  if (!page || !config) return <Spinner />

  const took = (next: AgentInstallShell) => {
    setLive(next)
    setConfig(next.settings)
    void query.invalidateQueries({ queryKey: ['settings', 'install_options'] })
  }

  const locked = !page.canWrite

  const submitGeneral = (event: FormEvent) => {
    event.preventDefault()
    setBusy('general')
    /* Bifele nebifate NU se trimit, ca in formularul vechi: serverul citeste
       lipsa lor ca "am scos-o". */
    const fields: Record<string, string> = {}
    if (config.auto_update) fields.auto_update = '1'
    if (config.enable_agent_uninstall) fields.enable_agent_uninstall = '1'

    void saveAgentInstallGeneral(fields)
      .then((next) => {
        setBusy('')
        took(next)
        toast('Settings saved', 'General Settings')
      })
      .catch((error: unknown) => {
        setBusy('')
        toast(failed(error), 'General Settings', 'error')
      })
  }

  const submitVisual = (event: FormEvent) => {
    event.preventDefault()
    setBusy('visual')
    const fields: Record<string, string> = { main_color: config.main_color }
    if (config.hide_on_system_tray) fields.hide_on_system_tray = '1'
    if (config.create_shortcut_on_desktop) fields.create_shortcut_on_desktop = '1'

    void saveAgentInstallVisual(fields)
      .then((next) => {
        setBusy('')
        took(next)
        toast('Settings saved', 'Visual Settings')
      })
      .catch((error: unknown) => {
        setBusy('')
        toast(failed(error), 'Visual Settings', 'error')
      })
  }

  return (
    <>
      <aside className="od-dark-sidebar od-module-rail">
        <ul className="od-dark-sidebar-nav default-sidebar-nav">
          {page.tabs.length > 0 ? <li className="menu-header">{page.tabs[0].group}</li> : null}
          {page.tabs.map((tab) => (
            <li className="menu-tab active" key={tab.id}>
              <a id={tab.id} href={`/app/${tab.href}`} onClick={(e) => e.preventDefault()}>
                <i className={tab.icon} aria-hidden="true"></i> {tab.label}
              </a>
            </li>
          ))}
          <li className="menu-block-divider"></li>
        </ul>
      </aside>
      <div className="page-content-wrapper">
        <div id="content">
          <h3 style={{ marginTop: 0 }}>Install Options</h3>

          <div className="row">
            <div className="col-md-4">
              <div className="panel panel-default">
                <div className="panel-heading">
                  <h3 className="panel-title">GENERAL</h3>
                </div>
                <form id="install-general-settings" onSubmit={submitGeneral}>
                  <table className="table table-hover" width="100%">
                    <tbody>
                      <Check
                        id="aiAutoUpdate"
                        label="Auto Update"
                        checked={config.auto_update}
                        disabled={locked}
                        onChange={(v) => setConfig({ ...config, auto_update: v })}
                      />
                      <Check
                        id="aiUninstall"
                        label="Uninstall Agent"
                        checked={config.enable_agent_uninstall}
                        disabled={locked}
                        onChange={(v) => setConfig({ ...config, enable_agent_uninstall: v })}
                      />
                    </tbody>
                  </table>
                  {page.canWrite ? (
                    <div className="form-group mb-0 p-2">
                      <button className="btn btn-success btn-block" type="submit" disabled={busy === 'general'}>
                        Save
                      </button>
                    </div>
                  ) : null}
                </form>
              </div>
            </div>

            <div className="col-md-4">
              <div className="panel panel-default">
                <div className="panel-heading">
                  <h3 className="panel-title">VISUAL SETTINGS</h3>
                </div>
                <form id="visual-settings" onSubmit={submitVisual}>
                  <table className="table table-hover" width="100%">
                    <tbody>
                      <Check
                        id="aiTray"
                        label="Hide on System Tray"
                        checked={config.hide_on_system_tray}
                        disabled={locked}
                        onChange={(v) => setConfig({ ...config, hide_on_system_tray: v })}
                      />
                      <Check
                        id="aiShortcut"
                        label="Create shortcut on Desktop"
                        checked={config.create_shortcut_on_desktop}
                        disabled={locked}
                        onChange={(v) => setConfig({ ...config, create_shortcut_on_desktop: v })}
                      />
                      <tr>
                        <td className="pl-2">
                          <label htmlFor="aiColor" style={{ fontWeight: 'normal', marginBottom: 0 }}>
                            Main Color
                          </label>
                        </td>
                        <td className="pr-2 align-right">
                          <input
                            type="text"
                            className="form-control"
                            id="aiColor"
                            placeholder="#DDDDDD"
                            value={config.main_color}
                            disabled={locked}
                            onChange={(e) => setConfig({ ...config, main_color: e.target.value })}
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  {page.canWrite ? (
                    <div className="form-group mb-0 p-2">
                      <button className="btn btn-success btn-block" type="submit" disabled={busy === 'visual'}>
                        Save
                      </button>
                    </div>
                  ) : null}
                </form>
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  )
}
